import React from "react";
import { Mail } from "lucide-react";
import Typography from "./shared/Typography";
import Button from "./shared/Button";
import AdSenseAd from "./AdSenseAd";
import { colors } from "../constants/colors";

const Support = () => {
  const contactMethods = [
    {
      icon: <Mail size={30} />,
      title: "Email Support",
      content: "support@nutriplate-ai.com",
      description: "Send us an email and we'll respond within 24 hours",
      action: "mailto:support@nutriplate-ai.com",
    },
  ];

  const containerStyle = {
    minHeight: "100vh",
    backgroundColor: colors.extraLightGray,
  };

  const heroSectionStyle = {
    background: `linear-gradient(135deg, ${colors.darkBlue} 0%, ${colors.darkGreen} 100%)`,
    color: colors.white,
    padding: "80px 20px 60px",
    textAlign: "center",
  };

  const contentSectionStyle = {
    padding: "80px 20px",
    maxWidth: "1200px",
    margin: "0 auto",
  };

  const contactGridStyle = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
    gap: "2rem",
    marginTop: "3rem",
  };

  const contactCardStyle = {
    backgroundColor: colors.white,
    padding: "2.5rem",
    borderRadius: "16px",
    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
    textAlign: "center",
    transition: "transform 0.3s ease, box-shadow 0.3s ease",
  };

  const faqSectionStyle = {
    backgroundColor: colors.white,
    padding: "3rem",
    borderRadius: "16px",
    marginTop: "3rem",
  };

  const faqItemStyle = {
    padding: "1.5rem",
    borderBottom: `1px solid ${colors.doubleExtraLightGray}`,
    marginBottom: "1rem",
  };

  const faqs = [
    {
      question: "How does the AI food recognition work?",
      answer:
        "Our AI analyzes photos of your food using advanced computer vision to identify ingredients and calculate nutritional information automatically.",
    },
    {
      question: "Can I sync with my fitness tracker?",
      answer:
        "Yes! NutriPlate integrates seamlessly with Apple Health and Google Fit to automatically track your exercise and activity data.",
    },
    {
      question: "Is my data secure?",
      answer:
        "Absolutely. We use industry-standard encryption and never share your personal data with third parties. Your privacy is our priority.",
    },
    {
      question: "How accurate are the nutrition calculations?",
      answer:
        "Our AI provides highly accurate nutrition data, with continuous improvements based on user feedback and updated food databases.",
    },
  ];

  return (
    <div style={containerStyle}>
      {/* Hero Section */}
      <section style={heroSectionStyle}>
        <div style={{ maxWidth: "800px", margin: "0 auto" }}>
          <Typography variant="h1" color={colors.white}>
            We're Here to Help
          </Typography>
          <Typography
            variant="subtitle"
            color={colors.white}
            style={{ opacity: 0.9, marginTop: "1rem" }}
          >
            Get support for NutriPlate and make the most of your nutrition
            tracking journey
          </Typography>
        </div>
      </section>

      {/* Contact Methods */}
      <section style={contentSectionStyle}>
        <div style={{ textAlign: "center", marginBottom: "2rem" }}>
          <Typography variant="h2" color={colors.darkBlue}>
            Contact Our Support Team
          </Typography>
        </div>

        <div style={contactGridStyle}>
          {contactMethods.map((method, index) => (
            <div
              key={index}
              style={contactCardStyle}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = "translateY(-5px)";
                e.currentTarget.style.boxShadow = "0 8px 30px rgba(0,0,0,0.12)";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 4px 20px rgba(0,0,0,0.08)";
              }}
            >
              <div style={{ color: colors.darkGreen, marginBottom: "1.5rem" }}>
                {method.icon}
              </div>
              <Typography
                variant="h3"
                color={colors.darkBlue}
                style={{ marginBottom: "0.5rem" }}
              >
                {method.title}
              </Typography>
              <Typography
                variant="body"
                color={colors.darkGreen}
                style={{ fontWeight: 600, marginBottom: "0.5rem" }}
              >
                {method.content}
              </Typography>
              <Typography
                variant="body"
                color={colors.darkGray}
                style={{ marginBottom: "1.5rem" }}
              >
                {method.description}
              </Typography>
              <Button
                variant="primary"
                size="medium"
                onClick={() => {
                  if (
                    method.action.startsWith("tel:") ||
                    method.action.startsWith("mailto:")
                  ) {
                    window.location.href = method.action;
                  }
                }}
              >
                {method.title === "Phone Support"
                  ? "Call Now"
                  : method.title === "Email Support"
                  ? "Send Email"
                  : "Start Chat"}
              </Button>
            </div>
          ))}
        </div>

        {/* Advertisement */}
        <div
          style={{
            padding: "2rem 0",
            textAlign: "center",
            backgroundColor: colors.extraLightGray,
            margin: "2rem 0",
            borderRadius: "12px",
          }}
        >
          <AdSenseAd
            adSlot="1234567891"
            style={{
              margin: "0 auto",
              maxWidth: "728px",
            }}
          />
        </div>

        {/* FAQ Section */}
        <div style={faqSectionStyle}>
          <Typography
            variant="h2"
            color={colors.darkBlue}
            style={{ textAlign: "center", marginBottom: "2rem" }}
          >
            Frequently Asked Questions
          </Typography>

          {faqs.map((faq, index) => (
            <div key={index} style={faqItemStyle}>
              <Typography
                variant="h3"
                color={colors.darkBlue}
                style={{ marginBottom: "0.5rem" }}
              >
                {faq.question}
              </Typography>
              <Typography variant="body" color={colors.darkGray}>
                {faq.answer}
              </Typography>
            </div>
          ))}
        </div>

        {/* Advertisement */}
        <div
          style={{
            padding: "2rem 0",
            textAlign: "center",
          }}
        >
          <AdSenseAd
            adSlot="1234567892"
            style={{
              margin: "0 auto",
              maxWidth: "728px",
            }}
          />
        </div>

        {/* Company Information */}
        <div
          style={{
            backgroundColor: colors.white,
            padding: "2rem",
            borderRadius: "16px",
            textAlign: "center",
            marginTop: "3rem",
            border: `2px solid ${colors.lightGreen}`,
            boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
          }}
        >
          <Typography
            variant="h3"
            color={colors.darkBlue}
            style={{ marginBottom: "1rem" }}
          >
            About NutriPlate
          </Typography>
          <Typography
            variant="body"
            color={colors.darkGray}
            style={{ marginBottom: "1.5rem", lineHeight: "1.6" }}
          >
            NutriPlate is developed and maintained by RayaTech AB, a Swedish
            technology company specializing in AI-powered health and nutrition
            solutions.
          </Typography>

          <div
            style={{
              backgroundColor: colors.lightBlue,
              padding: "1.5rem",
              borderRadius: "12px",
              border: `2px solid ${colors.darkBlue}`,
            }}
          >
            <Typography
              variant="body"
              style={{
                color: colors.darkBlue,
                fontWeight: "600",
                marginBottom: "0.5rem",
              }}
            >
              RayaTech AB
            </Typography>
            <Typography
              variant="body"
              style={{
                color: colors.darkGray,
                fontSize: "0.9rem",
                marginBottom: "0.3rem",
              }}
            >
              Organization Number: 559546-6904
            </Typography>
            <Typography
              variant="body"
              style={{
                color: colors.darkGray,
                fontSize: "0.9rem",
              }}
            >
              Stockholm, Sweden
            </Typography>
          </div>
        </div>

        {/* Additional Help */}
        <div
          style={{
            backgroundColor: colors.darkGreen,
            color: colors.white,
            padding: "3rem",
            borderRadius: "16px",
            textAlign: "center",
            marginTop: "3rem",
          }}
        >
          <Typography variant="h3" color={colors.white}>
            Still Need Help?
          </Typography>
          <Typography
            variant="body"
            color={colors.white}
            style={{ opacity: 0.9, margin: "1rem 0 2rem" }}
          >
            Our comprehensive help center has detailed guides and tutorials to
            help you get the most out of NutriPlate
          </Typography>
          <Button variant="secondary" size="large">
            Visit Help Center
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Support;
